import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipboardList, RefreshCw, Filter, Search } from 'lucide-react';
import './TaskHistoryTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const TaskHistoryTab = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

  useEffect(() => {
    if (token) {
      fetchTasks();
    }
  }, [token]);

  const fetchTasks = async () => {
    if (!auth) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/manager/tasks`, auth);
      if (res.data.success) {
        const data = Array.isArray(res.data.data)
          ? res.data.data
          : (res.data.data?.tasks || []);
        setTasks(data);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error('Error fetching task history:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const statusMatch = statusFilter === 'all' || task.status === statusFilter;
      const searchMatch = !search ||
        task.title?.toLowerCase().includes(search.toLowerCase()) ||
        task.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        String(task.room_number || '').includes(search);

      return statusMatch && searchMatch;
    });
  }, [tasks, statusFilter, search]);

  const getStatusClass = (status) => {
    if (status === 'completed') return 'status completed';
    if (status === 'in_progress') return 'status in-progress';
    if (status === 'cancelled') return 'status cancelled';
    return 'status assigned';
  };

  return (
    <div className="task-history-tab">
      <div className="content-card">
        <div className="card-header">
          <h2>
            <ClipboardList size={22} />
            Task History
          </h2>
          <button className="btn-icon" onClick={fetchTasks} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>

        <div className="history-filters">
          <div className="filter-item search-item">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by title, staff, room..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <Filter size={16} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spinning" size={30} />
            <p>Loading task history...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="empty-state compact">
            <ClipboardList size={48} />
            <h3>No tasks found</h3>
            <p>Assigned tasks will appear here once created.</p>
          </div>
        ) : (
          <div className="task-table-wrap">
            <table className="task-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Staff</th>
                  <th>Status</th>
                  <th>Assigned By</th>
                  <th>Priority</th>
                  <th>Room</th>
                  <th>Assigned At</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.task_id}>
                    <td>
                      <div className="task-title">{task.title}</div>
                      {task.description && <div className="task-description">{task.description}</div>}
                    </td>
                    <td>{task.employee_name || 'N/A'}</td>
                    <td>
                      <span className={getStatusClass(task.status)}>{(task.status || 'assigned').replace('_', ' ')}</span>
                    </td>
                    <td>{task.assigned_by_name || 'Manager'}</td>
                    <td>{task.priority ?? '-'}</td>
                    <td>{task.room_number ? `Room ${task.room_number}` : '-'}</td>
                    <td>{task.assigned_at ? new Date(task.assigned_at).toLocaleString() : '-'}</td>
                    <td>{task.due_date ? new Date(task.due_date).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskHistoryTab;
